import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import apiClient, { getAccountStatus } from '../services/api';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import './Dashboard.css';

function Dashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [accountStatus, setAccountStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [realtimeStats, setRealtimeStats] = useState(null);
  const [dailyTrends, setDailyTrends] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [socialCounters, setSocialCounters] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const loadAccountStatus = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getAccountStatus();
      setAccountStatus(response.data);
    } catch (err) {
      console.error('Failed to load account status:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRealtimeStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const [dashboardRes, logsRes, socialRes] = await Promise.all([
        apiClient.get('/api/v1/analytics/dashboard?days=7'),
        apiClient.get('/api/v1/analytics/agents/logs?days=7&limit=5'),
        apiClient.get('/api/v1/analytics/social-counters?days=7')
      ]);
      
      setRealtimeStats(dashboardRes.data);
      setSocialCounters(socialRes.data);
      
      // Format trends for chart - data is under daily_trends from dashboard endpoint
      const trendsData = dashboardRes.data?.daily_trends || [];
      const formattedTrends = trendsData.map(item => ({
        date: new Date(item.date).toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric' }),
        conversations: item.total_conversations || 0,
        avgSentiment: item.avg_sentiment ? (item.avg_sentiment * 100).toFixed(0) : 0
      }));
      setDailyTrends(formattedTrends);
      
      setRecentActivity(logsRes.data?.logs || []);
    } catch (err) {
      console.error('Failed to load realtime stats:', err);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  // Depend on ``user?.id`` (primitive) instead of ``user`` (object).
  // Redux assigns a new ``user`` reference on every ``/auth/me``
  // refresh dispatch, even when the underlying data is unchanged. With
  // the object dep, this effect would tear down and re-create the
  // 30-second polling interval on every dispatch, firing the analytics
  // endpoints multiple times per render and looking like an infinite
  // loop in the network tab. The primitive id only changes when a
  // different user is actually loaded.
  const userId = user?.id;
  useEffect(() => {
    if (userId) {
      loadAccountStatus();
      loadRealtimeStats();
      // Auto-refresh every 30 seconds
      const interval = setInterval(loadRealtimeStats, 30000);
      return () => clearInterval(interval);
    }
  }, [userId, loadAccountStatus, loadRealtimeStats]);

  // Listen for account deletion events to refresh status
  useEffect(() => {
    const handleDeletionScheduled = () => {
      loadAccountStatus();
    };

    const handleDeletionCancelled = () => {
      loadAccountStatus();
    };

    window.addEventListener('accountDeletionScheduled', handleDeletionScheduled);
    window.addEventListener('accountDeletionCancelled', handleDeletionCancelled);

    return () => {
      window.removeEventListener('accountDeletionScheduled', handleDeletionScheduled);
      window.removeEventListener('accountDeletionCancelled', handleDeletionCancelled);
    };
  }, [loadAccountStatus]);

  const isScheduledForDeletion = accountStatus?.has_active_deletion_request || false;

  const menuItems = [
    {
      title: t('dashboard.menu.whatsapp.title'),
      description: t('dashboard.menu.whatsapp.description'),
      path: '/whatsapp',
      iconClass: 'bi bi-whatsapp',
      badge: t('dashboard.menu.whatsapp.badge'),
      id: 'whatsapp'
    },
    {
      title: t('dashboard.menu.analytics.title'),
      description: t('dashboard.menu.analytics.description'),
      path: '/analytics',
      iconClass: 'bi bi-bar-chart-line-fill',
      id: 'analytics'
    },
    {
      title: t('dashboard.menu.agent_logs.title'),
      description: t('dashboard.menu.agent_logs.description'),
      path: '/analytics#logs',
      iconClass: 'bi bi-journal-text',
      id: 'agent-logs'
    },
    {
      title: t('dashboard.menu.agents_config.title'),
      description: t('dashboard.menu.agents_config.description'),
      path: '/agents',
      iconClass: 'bi bi-robot',
      id: 'agents'
    },
    {
      title: t('dashboard.menu.social.title', 'Social Accounts'),
      description: t('dashboard.menu.social.description', 'Connect and manage Instagram, Facebook, TikTok'),
      path: '/settings/social',
      iconClass: 'bi bi-instagram',
      id: 'social'
    },{
      title: t('dashboard.menu.posts.title'),
      description: t('dashboard.menu.posts.description'),
      path: '/posts',
      iconClass: 'bi bi-calendar-check',
      id: 'posts'
    },
    {
      title: t('dashboard.menu.conversations.title', 'Conversations'),
      description: t('dashboard.menu.conversations.description', 'View and manage all WhatsApp and Instagram DM conversations'),
      path: '/conversations',
      iconClass: 'bi bi-chat-dots-fill',
      id: 'conversations'
    },
    {
      title: t('dashboard.menu.templates.title', 'WhatsApp Templates'),
      description: t('dashboard.menu.templates.description', 'Manage message templates for appointments'),
      path: '/templates',
      iconClass: 'bi bi-file-text',
      id: 'templates'
    },
    {
      title: t('dashboard.menu.escalations.title'),
      description: t('dashboard.menu.escalations.description'),
      path: '/escalations',
      iconClass: 'bi bi-exclamation-triangle-fill',
      id: 'escalations'
    }
  ];

  return (
    <div className="container">
      <h1>{t('dashboard.title')}</h1>
      <div className="card">
        <h2>{t('dashboard.welcome.title')}</h2>
        <p>{t('dashboard.welcome.description')}</p>
      </div>

      {/* Realtime Activity Section */}
      <div className="card realtime-section">
        <div className="section-header">
          <h3>{t('dashboard.realtime.title', 'Realtime Activity')}</h3>
          <span className="live-badge">
            <span className="live-dot"></span>
            {t('dashboard.realtime.live', 'Live')}
          </span>
        </div>
        
        {statsLoading && !realtimeStats ? (
          <div className="loading-placeholder">{t('common.loading', 'Loading...')}</div>
        ) : (
          <>
            {/* Stats Grid */}
            <div className="stats-grid">
              <div className="stat-box stat-conversations">
                <i className="bi bi-chat-dots"></i>
                <div className="stat-content">
                  <span className="stat-value">{realtimeStats?.conversations?.total || 0}</span>
                  <span className="stat-label">{t('dashboard.realtime.conversations', 'Conversations')}</span>
                </div>
              </div>
              <div className="stat-box stat-messages">
                <i className="bi bi-envelope"></i>
                <div className="stat-content">
                  <span className="stat-value">{realtimeStats?.social_interactions?.total || 0}</span>
                  <span className="stat-label">{t('dashboard.realtime.interactions', 'Interactions')}</span>
                </div>
              </div>
              <div className="stat-box stat-sentiment">
                <i className="bi bi-emoji-smile"></i>
                <div className="stat-content">
                  <span className="stat-value">
                    {realtimeStats?.conversations?.average_sentiment_score ? (realtimeStats.conversations.average_sentiment_score * 100).toFixed(0) : 0}%
                  </span>
                  <span className="stat-label">{t('dashboard.realtime.avg_sentiment', 'Avg Sentiment')}</span>
                </div>
              </div>
              <div className="stat-box stat-escalations">
                <i className="bi bi-exclamation-triangle"></i>
                <div className="stat-content">
                  <span className="stat-value">{realtimeStats?.conversations?.escalations || 0}</span>
                  <span className="stat-label">{t('dashboard.realtime.escalations', 'Escalations')}</span>
                </div>
              </div>
              <div className="stat-box stat-response">
                <i className="bi bi-clock-history"></i>
                <div className="stat-content">
                  <span className="stat-value">
                    {realtimeStats?.conversations?.average_response_time_seconds ? `${(realtimeStats.conversations.average_response_time_seconds / 60).toFixed(1)}m` : '0m'}
                  </span>
                  <span className="stat-label">{t('dashboard.realtime.response_time', 'Avg Response')}</span>
                </div>
              </div>
            </div>

            {/* Social Media Counters */}
            {socialCounters && (
              <div className="social-counters-section">
                <h4>
                  📊
                  {' '}{t('dashboard.social_counters.title', 'Social Media Engagement')}
                </h4>
                <div className="stats-grid social-stats-grid">
                  <div className="stat-box stat-comments">
                    <i className="bi bi-chat-square-text"></i>
                    <div className="stat-content">
                      <span className="stat-value">{socialCounters.total_comments}</span>
                      <span className="stat-label">{t('dashboard.social_counters.total_comments', 'Comments')}</span>
                    </div>
                  </div>
                  <div className="stat-box stat-auto-replied">
                    <i className="bi bi-robot"></i>
                    <div className="stat-content">
                      <span className="stat-value">{socialCounters.auto_replied}</span>
                      <span className="stat-label">{t('dashboard.social_counters.auto_replied', 'AI Replies')}</span>
                    </div>
                  </div>
                  <div className="stat-box stat-unreplied">
                    <i className="bi bi-chat-left-dots"></i>
                    <div className="stat-content">
                      <span className="stat-value">{socialCounters.unreplied}</span>
                      <span className="stat-label">{t('dashboard.social_counters.unreplied', 'Unreplied')}</span>
                    </div>
                  </div>
                  <div className="stat-box stat-positive-social">
                    <i className="bi bi-hand-thumbs-up"></i>
                    <div className="stat-content">
                      <span className="stat-value">{socialCounters.sentiment?.positive || 0}</span>
                      <span className="stat-label">{t('analytics.sentiment.positive', 'Positive')}</span>
                    </div>
                  </div>
                  <div className="stat-box stat-negative-social">
                    <i className="bi bi-hand-thumbs-down"></i>
                    <div className="stat-content">
                      <span className="stat-value">{socialCounters.sentiment?.negative || 0}</span>
                      <span className="stat-label">{t('analytics.sentiment.negative', 'Negative')}</span>
                    </div>
                  </div>
                  <div className="stat-box stat-dm-conversations clickable" onClick={() => navigate('/conversations')} title={t('dashboard.social_counters.view_conversations', 'View conversations')}>
                    <i className="bi bi-envelope-paper"></i>
                    <div className="stat-content">
                      <span className="stat-value">{socialCounters.instagram_dm?.total_conversations || 0}</span>
                      <span className="stat-label">{t('dashboard.social_counters.dm_conversations', 'DM Conversations')}</span>
                    </div>
                  </div>
                  <div className="stat-box stat-dm-messages clickable" onClick={() => navigate('/conversations')} title={t('dashboard.social_counters.view_conversations', 'View conversations')}>
                    <i className="bi bi-chat-heart"></i>
                    <div className="stat-content">
                      <span className="stat-value">{socialCounters.instagram_dm?.total_messages || 0}</span>
                      <span className="stat-label">{t('dashboard.social_counters.dm_messages', 'DM Messages')}</span>
                    </div>
                  </div>
                  <div className="stat-box stat-fb-dm-conversations clickable" onClick={() => navigate('/conversations')} title={t('dashboard.social_counters.view_conversations', 'View conversations')}>
                    <i className="bi bi-facebook"></i>
                    <div className="stat-content">
                      <span className="stat-value">{socialCounters.facebook_messenger?.total_conversations || 0}</span>
                      <span className="stat-label">{t('dashboard.social_counters.fb_dm_conversations', 'FB Messenger Chats')}</span>
                    </div>
                  </div>
                  <div className="stat-box stat-fb-dm-messages clickable" onClick={() => navigate('/conversations')} title={t('dashboard.social_counters.view_conversations', 'View conversations')}>
                    <i className="bi bi-messenger"></i>
                    <div className="stat-content">
                      <span className="stat-value">{socialCounters.facebook_messenger?.total_messages || 0}</span>
                      <span className="stat-label">{t('dashboard.social_counters.fb_dm_messages', 'FB Messenger Msgs')}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Daily Trends Chart */}
            <div className="chart-card">
              <h4>{t('dashboard.chart.title', 'Daily Conversation Trends (7 days)')}</h4>
              {dailyTrends.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={dailyTrends}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis dataKey="date" stroke="#666" fontSize={12} />
                    <YAxis stroke="#666" fontSize={12} />
                    <Tooltip 
                      contentStyle={{ background: '#fff', border: '1px solid #ddd', borderRadius: '8px' }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="conversations" 
                      name={t('dashboard.chart.conversations', 'Conversations')}
                      stroke="#667eea" 
                      strokeWidth={3}
                      dot={{ fill: '#667eea', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, fill: '#764ba2' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="empty-chart">
                  <i className="bi bi-graph-up"></i>
                  <p>{t('dashboard.chart.no_data', 'No conversation data for this period')}</p>
                </div>
              )}
            </div>

            {/* Recent Activity */}
            <div className="recent-activity">
              <h4>{t('dashboard.recent.title', 'Recent Activity')}</h4>
              {recentActivity.length > 0 ? (
                <div className="activity-list">
                  {recentActivity.map((activity, index) => (
                    <div key={index} className="activity-item">
                      <div className="activity-icon">
                        {activity.agent_type === 'customer_care' ? (
                          <i className="bi bi-headset"></i>
                        ) : (
                          <i className="bi bi-mortarboard"></i>
                        )}
                      </div>
                      <div className="activity-content">
                        <span className="activity-contact">{activity.contact_name || t('dashboard.recent.unknown', 'Unknown')}</span>
                        <span className="activity-message">{activity.user_message?.substring(0, 50)}...</span>
                      </div>
                      <div className="activity-meta">
                        <span className={`sentiment-badge sentiment-${activity.sentiment > 0.5 ? 'positive' : activity.sentiment < 0.3 ? 'negative' : 'neutral'}`}>
                          {(activity.sentiment * 100).toFixed(0)}%
                        </span>
                        <span className="activity-time">
                          {new Date(activity.timestamp).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      {activity.id && (
                        <div className="activity-actions">
                          <button
                            className="btn-view-conversation"
                            onClick={() => navigate(`/analytics/conversation/${activity.id}`)}
                            title={t('dashboard.recent.view_conversation', 'View conversation')}
                          >
                            <i className="bi bi-eye"></i>
                          </button>
                          <button
                            className="btn-live-chat-activity"
                            onClick={() => navigate(`/live-chat/${activity.id}`)}
                            title={t('dashboard.recent.open_live_chat', 'Open Live Chat')}
                          >
                            <i className="bi bi-chat-dots"></i>
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-activity">
                  <i className="bi bi-inbox"></i>
                  <p>{t('dashboard.recent.no_activity', 'No recent activity')}</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <div className="card">
        <h3>{t('dashboard.quick_access')}</h3>
        <div className="menu-grid">
          {menuItems.map((item, index) => {
            const isDisabled = isScheduledForDeletion && item.id !== 'register';

            // Feature-flag gating
            const featureFlagMap = {
              'social': 'can_connect_socials',
              'posts': 'can_generate_posts',

            };
            const requiredFlag = featureFlagMap[item.id];
            const userFlags = user?.feature_flags || {};
            const isAdmin = user?.role === 'admin' || user?.role === 'superadmin' || user?.is_superuser;
            const isFeatureLocked = requiredFlag && !isAdmin && !userFlags[requiredFlag];

            const isFinallyDisabled = isDisabled || isFeatureLocked;

            return (
              <div 
                key={index} 
                className={`menu-item ${isFinallyDisabled ? 'menu-item-disabled' : ''}`}
                onClick={() => !isFinallyDisabled && navigate(item.path)}
                title={isDisabled ? t('dashboard.feature_unavailable') : isFeatureLocked ? t('dashboard.feature_locked', 'Contact your administrator to enable this feature') : ''}
              >
                <div className="menu-item-header">
                  <span className="menu-icon"><i className={item.iconClass}></i></span>
                  <h4>{item.title}</h4>
                  {item.badge && !isFinallyDisabled && <span className="badge badge-warning">{item.badge}</span>}
                  {isDisabled && <span className="badge badge-disabled">{t('dashboard.unavailable')}</span>}
                  {isFeatureLocked && !isDisabled && <span className="badge badge-locked">🔒</span>}
                </div>
                <p>{item.description}</p>
                {isFeatureLocked && !isDisabled && (
                  <p className="feature-locked-msg">
                    <span role="img" aria-label="locked">🔒</span>
                    {t('dashboard.contact_admin', 'Contact your administrator to enable this feature.')}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="card">
        <h3>{t('dashboard.getting_started.title')}</h3>
        <ol className="getting-started-list">
          <li>
            <strong>{t('dashboard.getting_started.step1.title')}</strong>
            <p>
              {t('dashboard.getting_started.step1.description_before')}<strong>"<i className="bi bi-whatsapp"></i> {t('dashboard.menu.whatsapp.title')}"</strong>{t('dashboard.getting_started.step1.description_after')}<strong>"{t('dashboard.getting_started.step1.connect_button')}"</strong>{t('dashboard.getting_started.step1.description_end')}
            </p>
          </li>
          <li>
            <strong>{t('dashboard.getting_started.step2.title')}</strong>
            <p>
              {t('dashboard.getting_started.step2.description')}
            </p>
          </li>
          
          <li>
            <strong>{t('dashboard.getting_started.step4.title')}</strong>
            <p>
              {t('dashboard.getting_started.step4.description')}
            </p>
            <ul style={{marginTop: '8px', marginLeft: '20px'}}>
              <li><strong>{t('dashboard.getting_started.step4.customer_care')}:</strong> {t('dashboard.getting_started.step4.customer_care_desc')}</li>
              <li><strong>{t('dashboard.getting_started.step4.tutor')}:</strong> {t('dashboard.getting_started.step4.tutor_desc')}</li>
            </ul>
            <p style={{marginTop: '8px', fontSize: '14px', color: '#666'}}>
              <em>{t('dashboard.getting_started.step4.hint')}</em>
            </p>
          </li>
          <li>
            <strong>{t('dashboard.getting_started.step5.title')}</strong>
            <p>{t('dashboard.getting_started.step5.description')}</p>
          </li>
          <li>
            <strong>{t('dashboard.getting_started.step6.title')}</strong>
            <p>{t('dashboard.getting_started.step6.description')}</p>
          </li>
        </ol>
        <footer className="page-footer">
          <Link to="/terms">{t('footer.terms')}</Link>
          <span className="page-footer-separator">·</span>
          <Link to="/privacy">{t('footer.privacy')}</Link>
        </footer>
      </div>
    </div>
  );
}

export default Dashboard;
